import React, { useEffect, useState } from 'react';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { CheckCircle, Copy, ExternalLink, Globe, AlertCircle, Loader2 } from 'lucide-react';

/**
 * Superadmin UI for publishing a specific landing page version to the
 * public `/welcome` route. Used to solicit prospective beta clients
 * without requiring them to sign in.
 */
const LandingPagePublish = () => {
  const [config, setConfig] = useState({ published: false, version: 'v3' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const publicUrl = `${window.location.origin}/welcome`;

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${getApiBase()}/admin/landing-config`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setConfig({
            published: Boolean(data.published),
            version: data.version || 'v3',
          });
        } else {
          setError('Failed to load landing page configuration.');
        }
      } catch (err) {
        console.error('Failed to load landing config:', err);
        setError('Failed to load landing page configuration.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const save = async (next) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${getApiBase()}/admin/landing-config`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(next),
      });
      if (res.ok) {
        const data = await res.json();
        setConfig({
          published: Boolean(data.published),
          version: data.version || 'v3',
        });
        setSuccess('Settings saved.');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const detail = await res.json().catch(() => ({}));
        setError(detail.detail || 'Failed to save landing page configuration.');
      }
    } catch (err) {
      console.error('Failed to save landing config:', err);
      setError('Failed to save landing page configuration.');
    } finally {
      setSaving(false);
    }
  };

  const selectVersion = (version) => save({ version });
  const togglePublished = () => save({ published: !config.published });

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setSuccess('Public URL copied to clipboard.');
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Failed to copy URL. Please copy manually.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const versions = [
    { id: 'v1', label: 'Landing Page (v1)', description: 'Original landing page' },
    { id: 'v2', label: 'Landing Page (v2)', description: 'Beta refresh' },
    { id: 'v3', label: 'Landing Page (v3)', description: 'Command Center framing (recommended)' },
  ];

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Publish Landing Page</h1>
        <p className="text-slate-600">
          Choose which landing page version to publish on the public <code>/welcome</code> route.
          Use this to share pricing and request forms with prospective clients without a login.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-green-700 text-sm">{success}</span>
        </div>
      )}

      {/* Publish status */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-5 h-5 text-slate-500" />
              <h2 className="text-lg font-semibold text-slate-900">Publication Status</h2>
            </div>
            <p className="text-sm text-slate-600">
              {config.published
                ? 'The landing page is publicly accessible.'
                : 'The landing page is not yet published. Visitors will see a "Coming Soon" message.'}
            </p>
          </div>
          <button
            onClick={togglePublished}
            disabled={saving}
            className={`px-5 py-2 rounded-lg font-medium text-white transition-colors disabled:opacity-50 ${
              config.published ? 'bg-slate-600 hover:bg-slate-700' : 'bg-purple-600 hover:bg-purple-700'
            }`}
          >
            {saving ? 'Saving...' : config.published ? 'Unpublish' : 'Publish'}
          </button>
        </div>

        <div className="mt-4 p-3 bg-slate-50 rounded-lg flex items-center justify-between gap-3">
          <code className="text-sm text-slate-700 truncate">{publicUrl}</code>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={copyUrl}
              className="p-2 rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-200 transition-colors"
              title="Copy public URL"
            >
              <Copy className="w-4 h-4" />
            </button>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-200 transition-colors"
              title="Open public URL"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Version selector */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-1">Published Version</h2>
        <p className="text-sm text-slate-600 mb-4">
          Select which version is rendered at <code>/welcome</code>.
        </p>
        <div className="space-y-3">
          {versions.map((v) => {
            const selected = config.version === v.id;
            return (
              <button
                key={v.id}
                onClick={() => selectVersion(v.id)}
                disabled={saving || selected}
                className={`w-full text-left p-4 rounded-lg border-2 transition-colors disabled:cursor-not-allowed ${
                  selected
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{v.label}</span>
                      {selected && (
                        <span className="text-xs font-medium text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full">
                          Selected
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5">{v.description}</p>
                  </div>
                  {selected && <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LandingPagePublish;
